import { BleTransport } from '../ble/transport';
import {
  isSweepEndLine,
  parseSweepPointLine,
  type SettingsStatus,
  type SweepPoint,
} from '../ble/toolbox-protocol';

export const SWEEP_COMPLETION_GRACE_MS = 900;
export const SWEEP_RUNNER_TICK_MS = 100;
export const SWEEP_DEBUG_HEARTBEAT_MS = 1000;

export type SweepSpec = {
  startHz: number;
  endHz: number;
  stepHz: number;
  dwellMs: number;
  timeoutMs: number;
};

export type SweepCompletionReason = 'sweepEnd' | 'hardware-state' | 'idle-gap';

export type SweepResult = {
  points: SweepPoint[];
  completionReason: SweepCompletionReason;
  startedAtMs: number;
  endedAtMs: number;
};

export type SweepPauseChangeDetails = {
  pointCount: number;
  lastFrequencyHz: number | null;
};

export type SweepPointProgressDetails = {
  pointCount: number;
  expectedPointCount: number;
  progressPercent: number | null;
};

export type SweepRunnerOptions = {
  signal?: AbortSignal;
  onStatus?: (message: string) => void;
  onDebug?: (message: string, details?: Record<string, unknown>) => void;
  onPoint?: (point: SweepPoint) => void;
  onPointProgress?: (details: SweepPointProgressDetails) => void;
  segmentStartStabilizationMs?: number;
  shouldPause?: () => boolean;
  onPauseChange?: (paused: boolean, details: SweepPauseChangeDetails) => void;
};

export async function runSweep(
  transport: BleTransport,
  spec: SweepSpec,
  options: SweepRunnerOptions = {}
): Promise<SweepResult> {
  const startedAtMs = Date.now();
  let endedAtMs = startedAtMs;
  let completionReason: SweepCompletionReason = 'sweepEnd';

  const points: SweepPoint[] = [];
  const segmentStartStabilizationMs = Math.max(0, options.segmentStartStabilizationMs ?? 0);
  const expectedPointCount = estimateSweepPointCount(spec);
  let sawSweepEnd = false;
  let hardwareSweepActive = Boolean(transport.getLatestSettingsStatus()?.sweepActive);
  let sawHardwareSweepActive = hardwareSweepActive;
  let segmentSawHardwareSweepActive = hardwareSweepActive;
  let lastLoggedSweepActive = hardwareSweepActive;
  let lastPointAtMs = 0;
  let lastPoint: SweepPoint | null = null;
  let reachedTerminalFrequency = false;
  let lastHeartbeatAtMs = startedAtMs;
  const direction: 1 | -1 = spec.endHz >= spec.startHz ? 1 : -1;
  const stepMagnitudeHz = Math.max(1, Math.abs(spec.stepHz));
  let nextSegmentStartHz = spec.startHz;
  let segmentActive = false;
  let pauseStopSent = false;
  let pauseInterruptActive = false;
  let ignoreSweepEnd = false;
  let pendingPausedStopSweepEnd = false;
  let awaitingResumedSegmentPoint = false;
  let paused = false;
  let pausedAtMs: number | null = null;
  let accumulatedPausedMs = 0;
  let lastSegmentStartAtMs = startedAtMs;
  let segmentStabilizationStartedAtMs: number | null = null;

  const unsubscribeLine = transport.onTextLine((line) => {
    options.onDebug?.('Sweep line received', { line });
    const point = parseSweepPointLine(line);
    if (point) {
      points.push(point);
      options.onPoint?.(point);
      const progressPercent =
        expectedPointCount > 0 ? Math.min(100, Math.round((points.length / expectedPointCount) * 100)) : null;
      options.onPointProgress?.({
        pointCount: points.length,
        expectedPointCount,
        progressPercent,
      });
      lastPoint = point;
      lastPointAtMs = Date.now();
      if (awaitingResumedSegmentPoint) {
        awaitingResumedSegmentPoint = false;
        pendingPausedStopSweepEnd = false;
        options.onDebug?.('Resumed segment produced first point; stale stop sweepEnd guard cleared');
      }
      reachedTerminalFrequency = reachedTerminalFrequency || isNearSweepEnd(point.frequencyHz, spec);
      options.onDebug?.('Sweep point received', {
        count: points.length,
        expectedPointCount,
        progressPercent,
        frequencyHz: point.frequencyHz,
        powerW: point.powerW,
      });
      return;
    }
    if (isSweepEndLine(line)) {
      if (ignoreSweepEnd) {
        options.onDebug?.('Sweep end marker ignored for paused segment');
        return;
      }
      if (pendingPausedStopSweepEnd) {
        pendingPausedStopSweepEnd = false;
        options.onDebug?.('Sweep end marker ignored for paused stop command');
        return;
      }
      sawSweepEnd = true;
      segmentActive = false;
      options.onDebug?.('Sweep end marker received');
    }
  });

  const unsubscribeSettings = transport.onSettingsStatus((settings: SettingsStatus) => {
    hardwareSweepActive = settings.sweepActive;
    if (settings.sweepActive) {
      sawHardwareSweepActive = true;
      if (segmentActive) {
        segmentSawHardwareSweepActive = true;
      }
    }
    if (hardwareSweepActive !== lastLoggedSweepActive) {
      lastLoggedSweepActive = hardwareSweepActive;
      options.onDebug?.('Hardware sweepActive changed', { sweepActive: hardwareSweepActive });
    }
  });

  try {
    options.onDebug?.('Starting sweep', {
      startHz: spec.startHz,
      endHz: spec.endHz,
      stepHz: spec.stepHz,
      dwellMs: spec.dwellMs,
      timeoutMs: spec.timeoutMs,
      expectedPointCount,
      segmentStartStabilizationMs,
      initialHardwareSweepActive: hardwareSweepActive,
    });

    while (true) {
      if (options.signal?.aborted) {
        if (segmentActive) {
          await transport.sendTextCommand('stop').catch(() => undefined);
        }
        throw new Error('Sweep canceled.');
      }

      const now = Date.now();
      const pausedWindowMs = pausedAtMs == null ? 0 : now - pausedAtMs;
      const elapsedMs = now - startedAtMs - accumulatedPausedMs - pausedWindowMs;
      const idleForMs = calculateIdleForMs(now, lastPointAtMs, lastSegmentStartAtMs);
      if (now - lastHeartbeatAtMs >= SWEEP_DEBUG_HEARTBEAT_MS) {
        lastHeartbeatAtMs = now;
        options.onDebug?.('Sweep heartbeat', {
          elapsedMs,
          timeoutMs: spec.timeoutMs,
          points: points.length,
          expectedPointCount,
          sawSweepEnd,
          hardwareSweepActive,
          sawHardwareSweepActive,
          reachedTerminalFrequency,
          lastPointFrequencyHz: lastPoint?.frequencyHz ?? null,
          lastPointPowerW: lastPoint?.powerW ?? null,
          idleForMs,
          segmentActive,
          paused,
          nextSegmentStartHz,
          pauseInterruptActive,
          segmentStartStabilizationMs,
          segmentStabilizationStartedAtMs,
        });
      }

      const shouldPause = options.shouldPause?.() ?? false;
      if (shouldPause !== paused) {
        paused = shouldPause;
        if (paused) {
          pausedAtMs = Date.now();
        } else if (pausedAtMs != null) {
          accumulatedPausedMs += Date.now() - pausedAtMs;
          pausedAtMs = null;
        }
        options.onPauseChange?.(paused, {
          pointCount: points.length,
          lastFrequencyHz: lastPoint?.frequencyHz ?? null,
        });
        options.onDebug?.(paused ? 'Sweep paused by gate' : 'Sweep resumed by gate', {
          pointCount: points.length,
          lastFrequencyHz: lastPoint?.frequencyHz ?? null,
        });
      }

      if (paused) {
        if (segmentStabilizationStartedAtMs != null) {
          segmentStabilizationStartedAtMs = null;
          options.onDebug?.('Sweep segment stabilization reset by pause gate');
        }
        if (segmentActive && !pauseStopSent) {
          pauseStopSent = true;
          pauseInterruptActive = true;
          ignoreSweepEnd = true;
          pendingPausedStopSweepEnd = true;
          segmentActive = false;
          options.onDebug?.('Stopping active sweep segment due to pause gate');
          await transport.sendTextCommand('stop').catch(() => undefined);
        }
        await delay(SWEEP_RUNNER_TICK_MS);
        continue;
      }

      if (sawSweepEnd && !pauseInterruptActive) {
        completionReason = 'sweepEnd';
        endedAtMs = now;
        options.onDebug?.('Sweep completed via text marker', {
          completionReason,
          points: points.length,
        });
        break;
      }

      if (!segmentActive) {
        nextSegmentStartHz = resolveNextSegmentStartHz(
          nextSegmentStartHz,
          lastPoint?.frequencyHz ?? null,
          direction,
          stepMagnitudeHz
        );

        if (hasRemainingSweepRange(nextSegmentStartHz, spec.endHz, direction)) {
          if (segmentStartStabilizationMs > 0) {
            if (segmentStabilizationStartedAtMs == null) {
              segmentStabilizationStartedAtMs = now;
              options.onDebug?.('Sweep segment stabilization started', {
                segmentStartHz: nextSegmentStartHz,
                stabilizationMs: segmentStartStabilizationMs,
              });
            }
            const stabilizationElapsedMs = now - segmentStabilizationStartedAtMs;
            if (stabilizationElapsedMs < segmentStartStabilizationMs) {
              await delay(SWEEP_RUNNER_TICK_MS);
              continue;
            }
            segmentStabilizationStartedAtMs = null;
            options.onDebug?.('Sweep segment stabilization complete', {
              segmentStartHz: nextSegmentStartHz,
              stabilizationMs: segmentStartStabilizationMs,
            });
          }
          options.onStatus?.(
            `Sweeping ${formatFrequencyHz(nextSegmentStartHz)} to ${formatFrequencyHz(spec.endHz)} Hz...`
          );
          const sweepCommand = `sweep ${nextSegmentStartHz} ${spec.endHz} ${spec.stepHz} ${spec.dwellMs}`;
          const resumed = points.length > 0 || pauseInterruptActive;
          pauseStopSent = false;
          pauseInterruptActive = false;
          ignoreSweepEnd = false;
          awaitingResumedSegmentPoint = resumed;
          if (!resumed) {
            pendingPausedStopSweepEnd = false;
          }
          sawSweepEnd = false;
          segmentActive = true;
          segmentSawHardwareSweepActive = hardwareSweepActive;
          lastSegmentStartAtMs = Date.now();
          options.onDebug?.('Sending sweep command', {
            command: sweepCommand,
            resumed,
            segmentStartHz: nextSegmentStartHz,
          });
          await transport.sendTextCommand(sweepCommand);
          options.onDebug?.('Sweep command sent', {
            segmentStartHz: nextSegmentStartHz,
          });
          await delay(SWEEP_RUNNER_TICK_MS);
          continue;
        }

        segmentStabilizationStartedAtMs = null;
        pauseInterruptActive = false;
      }

      if (points.length > 0 && !pauseInterruptActive) {
        if (reachedTerminalFrequency && idleForMs >= SWEEP_COMPLETION_GRACE_MS) {
          completionReason = 'idle-gap';
          endedAtMs = now;
          options.onDebug?.('Sweep completed near terminal frequency', {
            completionReason,
            points: points.length,
            idleForMs,
          });
          break;
        }

        if (segmentSawHardwareSweepActive && !hardwareSweepActive && idleForMs >= SWEEP_COMPLETION_GRACE_MS) {
          completionReason = 'hardware-state';
          endedAtMs = now;
          options.onDebug?.('Sweep completed via hardware state', {
            completionReason,
            points: points.length,
            idleForMs,
          });
          break;
        }

        if (!segmentActive && !hardwareSweepActive && idleForMs >= SWEEP_COMPLETION_GRACE_MS * 2) {
          completionReason = 'idle-gap';
          endedAtMs = now;
          options.onDebug?.('Sweep completed via idle fallback', {
            completionReason,
            points: points.length,
            idleForMs,
          });
          break;
        }
      }

      if (elapsedMs >= spec.timeoutMs) {
        options.onDebug?.('Sweep timeout reached', {
          elapsedMs,
          timeoutMs: spec.timeoutMs,
          points: points.length,
          expectedPointCount,
          sawSweepEnd,
          hardwareSweepActive,
          sawHardwareSweepActive,
          reachedTerminalFrequency,
          lastPointFrequencyHz: lastPoint?.frequencyHz ?? null,
          lastPointPowerW: lastPoint?.powerW ?? null,
          idleForMs,
          segmentActive,
          paused,
        });
        if (segmentActive) {
          await transport.sendTextCommand('stop').catch(() => undefined);
        }
        throw new Error('Sweep timed out.');
      }

      await delay(SWEEP_RUNNER_TICK_MS);
    }

    if (points.length === 0) {
      throw new Error('Sweep finished without any data points.');
    }

    options.onDebug?.('Sweep complete', {
      completionReason,
      points: points.length,
      expectedPointCount,
      lastPointFrequencyHz: lastPoint?.frequencyHz ?? null,
      lastPointPowerW: lastPoint?.powerW ?? null,
      durationMs: endedAtMs - startedAtMs,
    });

    return {
      points,
      completionReason,
      startedAtMs,
      endedAtMs,
    };
  } catch (error) {
    options.onDebug?.('Sweep failed', {
      error: error instanceof Error ? error.message : String(error),
      points: points.length,
      expectedPointCount,
      sawSweepEnd,
      hardwareSweepActive,
      sawHardwareSweepActive,
      reachedTerminalFrequency,
      lastPointFrequencyHz: lastPoint?.frequencyHz ?? null,
      lastPointPowerW: lastPoint?.powerW ?? null,
      segmentActive,
      paused,
    });
    throw error;
  } finally {
    unsubscribeLine();
    unsubscribeSettings();
  }
}

async function delay(timeoutMs: number): Promise<void> {
  await new Promise<void>((resolve) => {
    window.setTimeout(resolve, timeoutMs);
  });
}

function isNearSweepEnd(frequencyHz: number, spec: SweepSpec): boolean {
  const step = Math.abs(spec.stepHz) || 1;
  if (spec.endHz >= spec.startHz) {
    return frequencyHz >= spec.endHz - step;
  }
  return frequencyHz <= spec.endHz + step;
}

function resolveNextSegmentStartHz(
  fallbackStartHz: number,
  lastPointFrequencyHz: number | null,
  direction: 1 | -1,
  stepMagnitudeHz: number
): number {
  if (lastPointFrequencyHz == null || !Number.isFinite(lastPointFrequencyHz)) {
    return fallbackStartHz;
  }
  return Math.round(lastPointFrequencyHz + direction * stepMagnitudeHz);
}

function hasRemainingSweepRange(nextStartHz: number, endHz: number, direction: 1 | -1): boolean {
  if (direction > 0) {
    return nextStartHz <= endHz;
  }
  return nextStartHz >= endHz;
}

function estimateSweepPointCount(spec: SweepSpec): number {
  const step = Math.max(1, Math.abs(spec.stepHz));
  const range = Math.abs(spec.endHz - spec.startHz);
  if (!Number.isFinite(step) || !Number.isFinite(range)) {
    return 0;
  }
  return Math.floor(range / step) + 1;
}

function formatFrequencyHz(value: number): string {
  return Math.round(value).toLocaleString('en-US');
}

function calculateIdleForMs(nowMs: number, lastPointAtMs: number, lastSegmentStartAtMs: number): number {
  const referenceMs = Math.max(lastPointAtMs, lastSegmentStartAtMs);
  return nowMs - referenceMs;
}
